/* eslint-disable no-useless-catch */
/* eslint-disable no-undef */
import { ENV } from "../utils";

export class Event {
  baseApi = ENV.BASE_API;

  // ==================== ADMIN ENDPOINTS ====================

  /**
   * Get all events with filters (Admin)
   * @param {string} accessToken
   * @param {object} filters - { status, featured, categoryId, targetAudience, isVirtual, startDate, endDate, search, orderBy, orderDirection }
   * @param {number} page
   * @param {number} pageSize
   */
  async getEvents(accessToken, filters = {}, page = 1, pageSize = 10) {
    try {
      const queryParams = new URLSearchParams({
        page,
        pageSize,
        ...filters,
      }).toString();
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}?${queryParams}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get event by ID (Admin)
   * @param {string} accessToken
   * @param {number} id
   */
  async getEventById(accessToken, id) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}/${id}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create event (Admin)
   * @param {string} accessToken
   * @param {object} data
   */
  async createEvent(accessToken, data) {
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Handle image file separately
      if (data.imageFile) {
        formData.append("image", data.imageFile);
      }

      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}`;
      const params = {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update event (Admin)
   * @param {string} accessToken
   * @param {number} id
   * @param {object} data
   */
  async updateEvent(accessToken, id, data) {
    try {
      const formData = new FormData();
      Object.keys(data).forEach((key) => {
        if (data[key] !== null && data[key] !== undefined) {
          formData.append(key, data[key]);
        }
      });

      // Handle image file separately
      if (data.imageFile) {
        formData.append("image", data.imageFile);
      }

      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}/${id}`;
      const params = {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete event (Admin)
   * @param {string} accessToken
   * @param {number} id
   */
  async deleteEvent(accessToken, id) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}/${id}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get events statistics (Admin)
   * @param {string} accessToken
   */
  async getEventStats(accessToken) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT}/stats`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // ==================== EVENT CATEGORIES (ADMIN) ====================

  /**
   * Get all event categories (Admin)
   * @param {string} accessToken
   */
  async getEventCategories(accessToken) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT_CATEGORY}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Create event category (Admin)
   * @param {string} accessToken
   * @param {object} data
   */
  async createEventCategory(accessToken, data) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT_CATEGORY}`;
      const params = {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 201) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Update event category (Admin)
   * @param {string} accessToken
   * @param {number} id
   * @param {object} data
   */
  async updateEventCategory(accessToken, id, data) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT_CATEGORY}/${id}`;
      const params = {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(data),
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete event category (Admin)
   * @param {string} accessToken
   * @param {number} id
   */
  async deleteEventCategory(accessToken, id) {
    try {
      const url = `${this.baseApi}/admin/${ENV.API_ROUTES.EVENT_CATEGORY}/${id}`;
      const params = {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // ==================== AUTHENTICATED USER ENDPOINTS ====================

  /**
   * Get upcoming events (Authenticated users)
   * @param {string} accessToken
   * @param {number} limit
   */
  async getUpcomingEvents(accessToken, limit = 10) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/upcoming?limit=${limit}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get featured events (Authenticated users)
   * @param {string} accessToken
   * @param {number} limit
   */
  async getFeaturedEvents(accessToken, limit = 5) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/featured?limit=${limit}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get past events (Authenticated users)
   * @param {string} accessToken
   * @param {number} page
   * @param {number} pageSize
   */
  async getPastEvents(accessToken, page = 1, pageSize = 10) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/past?page=${page}&pageSize=${pageSize}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get events by category (Authenticated users)
   * @param {string} accessToken
   * @param {number} categoryId
   * @param {number} page
   * @param {number} pageSize
   */
  async getEventsByCategory(accessToken, categoryId, page = 1, pageSize = 10) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/category/${categoryId}?page=${page}&pageSize=${pageSize}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get event by slug (Authenticated users)
   * @param {string} accessToken
   * @param {string} slug
   */
  async getEventBySlug(accessToken, slug) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/slug/${slug}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get categories for authenticated users
   * @param {string} accessToken
   */
  async getCategoriesAuth(accessToken) {
    try {
      const url = `${this.baseApi}/${ENV.API_ROUTES.EVENT}/categories`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  // ==================== PUBLIC ENDPOINTS ====================

  /**
   * Get public events
   * @param {number} page
   * @param {number} pageSize
   */
  async getPublicEvents(page = 1, pageSize = 12) {
    try {
      const url = `${this.baseApi}/public/${ENV.API_ROUTES.EVENT}?page=${page}&pageSize=${pageSize}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get featured public events
   * @param {number} limit
   */
  async getPublicFeaturedEvents(limit = 6) {
    try {
      const url = `${this.baseApi}/public/${ENV.API_ROUTES.EVENT}/featured?limit=${limit}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get public event by slug
   * @param {string} slug
   */
  async getPublicEventBySlug(slug) {
    try {
      const url = `${this.baseApi}/public/${ENV.API_ROUTES.EVENT}/slug/${slug}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get events for landing page calendar
   * @param {number} year - Optional year (defaults to current year)
   * @param {number} month - Optional month 1-12 (defaults to current month)
   */
  async getLandingEvents(year, month) {
    try {
      const queryParams = new URLSearchParams();
      if (year) queryParams.append("year", year);
      if (month) queryParams.append("month", month);

      const queryString = queryParams.toString();
      const url = `${this.baseApi}/public/${ENV.API_ROUTES.EVENT}/landing${
        queryString ? `?${queryString}` : ""
      }`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get public event categories
   */
  async getPublicCategories() {
    try {
      const url = `${this.baseApi}/public/${ENV.API_ROUTES.EVENT_CATEGORY}`;
      const params = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      };
      const response = await fetch(url, params);
      const result = await response.json();
      if (response.status !== 200) throw result;
      return result;
    } catch (error) {
      throw error;
    }
  }
}
